// Sounds outside the table (D-203): a few short chimes synthesised with Web Audio, after cardsclassic's lib/sound.ts,
// so they need no files and start at once. They share the table's AudioContext (screens/play/sound.ts) so the first
// tap that unlocks one unlocks both.
//
// coin       the daily token is claimed: a bright two-coin clink rising
// chip       a Shop purchase or a free item claimed: shells clinking, then `success` on top
// success    a soft major third, the "done" under a purchase
// notify     a new notification arrives live: one quiet bell
// celebrate  a badge earned, "It's yours", a friend request accepted: a short mallet run to a sparkle
// call       your tournament table is ready: the turn chime twice, so it is heard from another page
//
// Every chime follows the master Sound switch only (prefs.sound, D-109); the table's finer switches are about the table.
// A page that has not been tapped yet stays quiet rather than queueing a chime to play late (audioUnlock.ts, D-108).
import { audioUnlocked } from "../screens/play/audioUnlock";
import { audioContext, soundOn } from "../screens/play/sound";

export const CHIMES = ["coin", "chip", "success", "notify", "celebrate", "call"] as const;
export type Chime = (typeof CHIMES)[number];

/** Whether chimes would play now: the master switch is on. */
export const chimeAllowed = (): boolean => soundOn();

let master: GainNode | null = null;
let masterOf: AudioContext | null = null;
let last: Partial<Record<Chime, number>> = {};

/** A gentle compressor on the way out so a chip and a success stacked together don't clip. */
function out(c: AudioContext): AudioNode {
  if (master && masterOf === c) return master;
  master = c.createGain();
  master.gain.value = 0.8;
  const comp = c.createDynamicsCompressor();
  comp.threshold.value = -16; comp.knee.value = 12; comp.ratio.value = 3; comp.attack.value = 0.004; comp.release.value = 0.18;
  master.connect(comp).connect(c.destination);
  masterOf = c;
  return master;
}

function tone(c: AudioContext, o: AudioNode, t: number, freq: number, dur: number, peak: number, type: OscillatorType = "sine", attack = 0.006): void {
  const osc = c.createOscillator();
  const g = c.createGain();
  osc.type = type;
  osc.frequency.setValueAtTime(freq, t);
  g.gain.setValueAtTime(0.0001, t);
  g.gain.exponentialRampToValueAtTime(Math.max(0.0002, peak), t + attack);
  g.gain.exponentialRampToValueAtTime(0.0001, t + attack + dur);
  osc.connect(g).connect(o);
  osc.start(t);
  osc.stop(t + attack + dur + 0.05);
}
/** Marimba-ish: a sine with a quiet fourth partial that dies fast. */
const mallet = (c: AudioContext, o: AudioNode, t: number, f: number, peak = 0.3, dur = 0.5): void => { tone(c, o, t, f, dur, peak); tone(c, o, t, f * 4, 0.08, peak * 0.25); };

const VOICES: Record<Chime, (c: AudioContext, o: AudioNode, t: number) => void> = {
  coin: (c, o, t) => {
    // Two coins: B6 then E7, each a bright triangle with a ringing partial.
    for (const [i, f] of [1975.5, 2637].entries()) {
      tone(c, o, t + i * 0.08, f, 0.22, 0.12, "triangle", 0.002);
      tone(c, o, t + i * 0.08, f * 2.01, 0.09, 0.04, "sine", 0.002);
    }
  },
  chip: (c, o, t) => {
    // Shells clinking: three bright knocks, a little uneven.
    for (let i = 0; i < 3; i++) tone(c, o, t + i * (0.05 + (i % 2) * 0.015), 2400 + i * 330, 0.07, 0.11, "triangle", 0.002);
  },
  success: (c, o, t) => { mallet(c, o, t, 783.99, 0.2); mallet(c, o, t + 0.1, 987.77, 0.2, 0.6); },
  notify: (c, o, t) => { tone(c, o, t, 880, 0.6, 0.12); tone(c, o, t, 1760, 0.3, 0.035); tone(c, o, t + 0.13, 1318.5, 0.7, 0.09); },
  celebrate: (c, o, t) => {
    [523.25, 659.25, 783.99, 1046.5].forEach((f, i) => mallet(c, o, t + i * 0.09, f, 0.18, i === 3 ? 0.8 : 0.4));
    for (let i = 0; i < 5; i++) tone(c, o, t + 0.36 + i * 0.045, 2093 + i * 190, 0.18, 0.035, "triangle");
  },
  call: (c, o, t) => { for (const at of [0, 0.42]) { mallet(c, o, t + at, 659.25, 0.28); mallet(c, o, t + at + 0.12, 987.77, 0.26); } },
};

/** Plays one chime. Returns whether it was scheduled (tests; callers never need it). */
export function chime(name: Chime, opts: { delayMs?: number } = {}): boolean {
  if (!chimeAllowed()) return false;
  const c = audioContext();
  if (!c) return false;
  // Before any tap the browser keeps the context suspended; a chime scheduled now would play late, on the first tap.
  if (c.state !== "running") { if (!audioUnlocked()) return false; void c.resume().catch(() => undefined); }
  // The same chime twice in one moment (two notices in one message) plays once.
  const now = Date.now();
  if (!opts.delayMs && (last[name] ?? -Infinity) > now - 60) return false;
  last[name] = now;
  try { VOICES[name](c, out(c), c.currentTime + 0.005 + Math.max(0, (opts.delayMs ?? 0) / 1000)); return true; }
  catch { return false; }
}

/** The moments, named for their callers. */
export const coinChime = (): void => { chime("coin"); };
export const purchaseChime = (): void => { chime("chip"); chime("success", { delayMs: 140 }); };

/** Tests. */
export function _resetChimes(): void { master = null; masterOf = null; last = {}; }
